import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Notification } from '../../types';

// Import services directly
import { useErrorNotifications } from '../../hooks/useErrorNotifications';
import { notificationService } from '../../services/notifications';

const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { notifications: errorNotifications, unreadCount: errorUnreadCount, markAsRead: markErrorAsRead, markAllAsRead: markAllErrorsAsRead } = useErrorNotifications();
  
  // Regular notifications state
  const [regularNotifications, setRegularNotifications] = useState<Notification[]>([]);
  const [regularUnreadCount, setRegularUnreadCount] = useState(0);
  
  // Use error notifications for developers, regular notifications for others
  const isDeveloper = user?.role === 'developer';
  const notifications = isDeveloper ? errorNotifications : regularNotifications;
  const unreadCount = isDeveloper ? errorUnreadCount : regularUnreadCount;
  
  // Load regular notifications for non-developers
  useEffect(() => {
    if (!isDeveloper) {
      loadRegularNotifications();
    }
  }, [isDeveloper]);
  
  const loadRegularNotifications = async () => {
    try {
      const data = await notificationService.getNotifications({ limit: 10 });
      setRegularNotifications(data);
      setRegularUnreadCount(data.filter(n => !n.isRead).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };
  
  const markAsRead = async (notificationId: string) => {
    if (isDeveloper) {
      markErrorAsRead(notificationId);
    } else {
      try {
        await notificationService.markAsRead(notificationId);
        setRegularNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
        );
        setRegularUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };
  
  const markAllAsRead = async () => {
    if (isDeveloper) {
      markAllErrorsAsRead();
    } else {
      try {
        await notificationService.markAllAsRead();
        setRegularNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setRegularUnreadCount(0);
      } catch (error) {
        console.error('Failed to mark all as read:', error);
      }
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 transform -translate-x-3/4 sm:-translate-x-0">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Notifications
                </h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No notifications
                </div>
              ) : (
                notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => markAsRead(isDeveloper ? (notification as any).error_id : notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      {isDeveloper ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor((notification as any).severity)}`}>
                          {(notification as any).severity}
                        </span>
                      ) : (
                        <span className="text-2xl flex-shrink-0">
                          {(notification as Notification).type === 'urgent' ? '🚨' : 
                           (notification as Notification).type === 'warning' ? '⚠️' : 
                           (notification as Notification).type === 'success' ? '✅' : 'ℹ️'}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {new Date(isDeveloper ? (notification as any).created_at : (notification as Notification).createdAt).toLocaleString()}
                        </p>
                        {!isDeveloper && (notification as Notification).actionUrl && (notification as Notification).actionLabel && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const actionUrl = (notification as Notification).actionUrl;
                              if (actionUrl?.startsWith('#')) {
                                const tab = actionUrl.substring(1);
                                window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: { tab } }));
                              } else if (actionUrl) {
                                window.open(actionUrl, '_blank');
                              }
                              if (!(notification as Notification).isRead) {
                                markAsRead(notification.id);
                              }
                              setIsOpen(false);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                          >
                            {(notification as Notification).actionLabel}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 10 && (
              <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700">
                <button className="text-sm text-blue-600 hover:text-blue-800">
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;